import { Field, Form, Formik, useFormikContext } from 'formik'
import React, { useEffect, useState } from 'react'
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic'
import { OfferionErrorMessage } from '../../../shared/forms/validation/OfferionErrorMessage'
import { toastSuccess, toastWarning } from '../../../shared/toastr'
import { OfferionTooltip } from '../../../shared/tooltip'
import { CompanySettingsApi } from '../../../services/CompanySettingsApi'
import { EReceiptType, UpdateEInvoiceConfigurationResult } from '../../../shared/models'
import * as Yup from 'yup'
import { useNavigate } from 'react-router-dom'
import { useOfferionTranslation } from '../../../shared/store/hooks/useOfferionTranslation'
import { useCompanyStore } from '../../../shared/store/companyStore'
import { CollapsibleSection } from '../../ui/CollapsibleSection'
import { ResizeableTextarea } from '../../../shared/forms/ResizeableTextarea'
import { ClassificationCodeInput } from '../../../v2/shared/form/ClassificationCodeInput'

export const EInvoiceSettingsContainer = () => {
    const { t } = useOfferionTranslation()
    const navigate = useNavigate()

    const [configuration, setConfiguration] = useState<{
        eReceiptUserName: string
        legalCompanyName: string
        eReceiptPassword: string
        eReceiptType?: EReceiptType
    } | null>()

    const [serviceMode, setServiceMode] = useState<EReceiptType>(EReceiptType.none)
    const country = useCompanyStore(state => state.companySettings.country)

    const ServiceSelector = () => {
        const formik = useFormikContext<any>()

        const switchMode = (mode: EReceiptType) => {
            if (mode === serviceMode) return

            if (formik) {
                if (formik.setTouched) formik.setTouched({}, false)
                if (formik.setErrors) formik.setErrors({})
                if (formik.setFieldValue) {
                    formik.setFieldValue('eReceiptUserName', '', false)
                    formik.setFieldValue('eReceiptPassword', '', false)
                }
                if (formik.resetForm) {
                    formik.resetForm({
                        values: { ...formik.values, eReceiptUserName: '', eReceiptPassword: '' },
                        errors: {},
                        touched: {},
                    })
                }
            }

            setServiceMode(mode)
        }

        return (
            <div
                className="info__row-content info__row-content--with-button"
                style={{ display: 'flex', paddingLeft: '1rem', alignItems: 'center', gap: 24 }}
            >
                <label
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                    }}
                >
                    <input
                        aria-label={t('Settings.EInvoice.Table.mode.eReceipt')}
                        type="radio"
                        name="serviceMode"
                        value={EReceiptType.mojERacun}
                        checked={serviceMode === EReceiptType.mojERacun}
                        onChange={() => switchMode(EReceiptType.mojERacun)}
                        style={{ margin: 0, width: 'unset' }}
                    />
                    <span style={{ marginLeft: 8 }}>{t('Settings.EInvoice.Table.mode.eReceipt')}</span>
                </label>

                <label
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                    }}
                >
                    <input
                        aria-label={t('Settings.EInvoice.Table.mode.ePoslovanje')}
                        type="radio"
                        name="serviceMode"
                        value={EReceiptType.ePoslovanje}
                        checked={serviceMode === EReceiptType.ePoslovanje}
                        onChange={() => switchMode(EReceiptType.ePoslovanje)}
                        style={{ margin: 0, width: 'unset' }}
                    />
                    <span style={{ marginLeft: 8 }}>{t('Settings.EInvoice.Table.mode.ePoslovanje')}</span>
                </label>
            </div>
        )
    }

    useEffect(() => {
        CompanySettingsApi.GetEInvoiceConfiguration().then(result => {
            setServiceMode((result as any).eReceiptType ?? EReceiptType.none)
            setConfiguration({ ...result, eReceiptPassword: '' })
        })
    }, [])

    const shouldRequirePassword = (serviceMode: EReceiptType, configuration: any, values: any) => {
        if (serviceMode === EReceiptType.none) return false

        const existingType: EReceiptType = (configuration && configuration.eReceiptType) ?? EReceiptType.none
        const existingUser: string | undefined = configuration && configuration.eReceiptUserName

        const hasExistingConfig = !!existingUser && existingType !== EReceiptType.none

        const currentUser: string | undefined = values && values.eReceiptUserName

        const usernameChanged = !!existingUser && !!currentUser && existingUser !== currentUser
        const modeChanged = existingType !== serviceMode

        if (!hasExistingConfig) return true

        if (usernameChanged || modeChanged) return true

        return false
    }

    const saveConfiguration = (
        model: {
            defaultClassificationCode: string
            eReceiptUserName: string
            legalCompanyName: string
            eReceiptPassword: string
            [key: string]: any
        },
        formikHelpers?: any
    ) => {
        const requiresPassword = shouldRequirePassword(serviceMode, configuration, model)

        if (requiresPassword) {
            const hasPassword = !!(model.eReceiptPassword && model.eReceiptPassword.trim())
            if (!hasPassword) {
                const msg = t('Allaround.Validation.required', { fieldName: t('Settings.EInvoice.Table.password') })
                if (formikHelpers && formikHelpers.setFieldError) {
                    formikHelpers.setFieldTouched('eReceiptPassword', true, false)
                    formikHelpers.setFieldError('eReceiptPassword', msg)
                }
                return
            }
        }

        const payload: any = {
            ...model,
            eReceiptType: serviceMode,
        }

        if (!requiresPassword && (!model.eReceiptPassword || !model.eReceiptPassword.trim())) {
            delete payload.eReceiptPassword
        }

        CompanySettingsApi.SaveEInvoiceConfiguration(payload).then(result => {
            switch (result) {
                case UpdateEInvoiceConfigurationResult.Success:
                    toastSuccess('Settings.EInvoice.Messages.successSave')
                    setConfiguration(prev => ({
                        ...(prev || {}),
                        ...model,
                        eReceiptType: serviceMode,
                        eReceiptPassword: '',
                    }))
                    break

                case UpdateEInvoiceConfigurationResult.WrongLoginInformation:
                    toastWarning('Settings.EInvoice.Messages.errorSave')
                    break

                case UpdateEInvoiceConfigurationResult.CompanyMissingOib:
                case UpdateEInvoiceConfigurationResult.CompanyMissingCountry:
                    toastWarning('Settings.EInvoice.Messages.warningMissingCompanyData')

                    break
            }
        })
    }

    if (!configuration) return null

    const validateUsername = (serviceMode: EReceiptType, t: any) => {
        const fieldKey = serviceMode === EReceiptType.ePoslovanje ? 'Settings.EInvoice.Table.userID' : 'Settings.EInvoice.Table.userEmail'

        let schema: any = Yup.string()
            .nullable()
            .test('required-when-service-selected', t('Allaround.Validation.required', { fieldName: t(fieldKey) }), function (value) {
                if (serviceMode === EReceiptType.none) return true
                return !!value
            })

        return schema
    }

    const validatePassword = (serviceMode: EReceiptType, configuration: any, t: any) => {
        return Yup.string()
            .nullable()
            .test(
                'password-required-when-provider-selected',
                t('Allaround.Validation.required', { fieldName: t('Settings.EInvoice.Table.password') }),
                function (value) {
                    const values = this.parent as any

                    if (!shouldRequirePassword(serviceMode, configuration, values)) {
                        return true
                    }

                    return !!(value && value.trim())
                }
            )
    }

    const schema = Yup.object().shape({
        eReceiptUserName: validateUsername(serviceMode, t),
        legalCompanyName: Yup.string()
            .required(t('Allaround.Validation.required', { fieldName: t('Settings.EInvoice.Table.obligatoryInvoiceData') }))
            .nullable(),
        eReceiptPassword: validatePassword(serviceMode, configuration, t),
        defaultClassificationCode:
            country === 'HR'
                ? Yup.string()
                      .matches(/^\d{2}\.\d{2}\.\d{2}$/, t('Allaround.Validation.classificationCodeFormat'))
                      .nullable()
                : Yup.string().nullable(),
    })

    let serviceKeys = {
        titleKey: 'Settings.EInvoice.Table.MyEInvoice.title',
        tipTitleKey: 'Settings.EInvoice.Table.MyEInvoice.Title.infoTitle',
        tipBodyKey: 'Settings.EInvoice.Table.MyEInvoice.infoText',
    }

    if (serviceMode === EReceiptType.ePoslovanje) {
        serviceKeys = {
            titleKey: 'Settings.EInvoice.Table.MyEPoslovanje.title',
            tipTitleKey: 'Settings.EInvoice.Table.MyEPoslovanje.Title.infoTitle',
            tipBodyKey: 'Settings.EInvoice.Table.MyEPoslovanje.infoText',
        }
    } else if (serviceMode === EReceiptType.mojERacun) {
        serviceKeys = {
            titleKey: 'Settings.EInvoice.Table.MyEInvoice.title',
            tipTitleKey: 'Settings.EInvoice.Table.MyEInvoice.Title.infoTitle',
            tipBodyKey: 'Settings.EInvoice.Table.MyEInvoice.infoText',
        }
    }

    return (
        <main className="main">
            <BreadcrumbsItem to="/e-invoice">{t('Settings.EInvoice.Header.pageTitle')}</BreadcrumbsItem>

            <Formik validationSchema={schema} onSubmit={saveConfiguration} initialValues={configuration} validateOnMount={false}>
                <Form noValidate={true}>
                    <div className="main__header">
                        <div className="main-title">{t('Settings.EInvoice.Header.pageTitle')}</div>

                        <div className="main__header-buttons main__header-buttons--show-all">
                            <div className="button-outer">
                                <button className="button button--white button--padded" type="submit">
                                    <span className="icon icon__save"></span>
                                    <span className="button-text">{t('Allaround.Button.save')}</span>
                                </button>
                            </div>
                            <div className="button-outer">
                                <button className="button button--white button--padded" type="button" onClick={() => navigate(-1)}>
                                    <span className="icon icon__delete"></span>
                                    <span className="button-text">{t('Allaround.Button.cancel')}</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="table__container curled-paper settings pt3">
                        <div className="info__row">
                            <label className="info__row-name">{t('Settings.EInvoice.Table.serviceType')}</label>
                            <ServiceSelector />
                        </div>

                        {serviceMode !== EReceiptType.none && (
                            <CollapsibleSection
                                isOpenByDefault={true}
                                title={
                                    <div>
                                        <span>{t(serviceKeys.titleKey)}</span>
                                        <OfferionTooltip
                                            iconStyle={{ marginBottom: '7px' }}
                                            titleKey={serviceKeys.tipTitleKey}
                                            bodyKey={serviceKeys.tipBodyKey}
                                        />
                                    </div>
                                }
                            >
                                {serviceMode === EReceiptType.ePoslovanje && (
                                    <div className="info__row info__row--centered">
                                        <label style={{ paddingRight: '1rem' }} className="info__row-name">
                                            {t('Settings.EInvoice.Table.EPoslovanjeRegistrationLabel')}
                                        </label>
                                        <div className="info__row-content info__row-content--with-button">
                                            <a
                                                href={t('Settings.EInvoice.Table.EPoslovanjeRegistrationUrl')}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {t('Settings.EInvoice.Table.EPoslovanjeRegistrationUrl')}
                                            </a>
                                        </div>
                                    </div>
                                )}

                                {serviceMode === EReceiptType.mojERacun && (
                                    <div className="info__row info__row--centered">
                                        <label style={{ paddingRight: '1rem' }} className="info__row-name">
                                            {t('Settings.EInvoice.Table.MojERacunRegistrationLabel')}
                                        </label>
                                        <div className="info__row-content info__row-content--with-button">
                                            <a
                                                href={t('Settings.EInvoice.Table.MojERacunRegistrationUrl')}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {t('Settings.EInvoice.Table.MojERacunRegistrationUrl')}
                                            </a>
                                        </div>
                                    </div>
                                )}

                                <div className="info__row">
                                    <label className="info__row-name" htmlFor="">
                                        {t(
                                            serviceMode === EReceiptType.ePoslovanje
                                                ? 'Settings.EInvoice.Table.userID'
                                                : 'Settings.EInvoice.Table.userEmail'
                                        )}{' '}
                                        {serviceMode === EReceiptType.ePoslovanje
                                            ? t('Settings.EInvoice.Table.mode.ePoslovanje')
                                            : serviceMode === EReceiptType.mojERacun
                                              ? t('Settings.EInvoice.Table.mode.eReceipt')
                                              : ''}{' '}
                                        *
                                    </label>
                                    <div className="info__row-content info__row-content--with-button">
                                        <Field name="eReceiptUserName" type="text" />
                                    </div>
                                    <OfferionErrorMessage name="eReceiptUserName" />
                                </div>
                                <div className="info__row">
                                    <label className="info__row-name" htmlFor="">
                                        {t('Settings.EInvoice.Table.password')}
                                    </label>
                                    <div className="info__row-content info__row-content--with-button">
                                        <Field name="eReceiptPassword" type="text" autoComplete="off" />
                                    </div>
                                    <OfferionErrorMessage name="eReceiptPassword" />
                                </div>
                            </CollapsibleSection>
                        )}

                        <CollapsibleSection title={<span>{t('Settings.EInvoice.Table.ObligatoryData.title')}</span>} isOpenByDefault={true}>
                            <div className="info__row">
                                <label style={{ wordWrap: 'break-word' }} className="info__row-name mod-padding-right-6" htmlFor="">
                                    {t('Settings.EInvoice.Table.obligatoryInvoiceData')} *
                                    <OfferionTooltip
                                        iconStyle={{ marginBottom: '7px' }}
                                        titleKey="Settings.EInvoice.Table.ObligatoryInvoiceData.infoTitle"
                                        bodyKey="Settings.EInvoice.Table.ObligatoryInvoiceData.infoText"
                                    />
                                </label>
                                <div className="info__row-content info__row-content--with-button">
                                    <Field style={{ width: '100%' }} component={ResizeableTextarea} name="legalCompanyName" />
                                    <OfferionErrorMessage name="legalCompanyName" />
                                </div>
                            </div>
                        </CollapsibleSection>

                        <CollapsibleSection title={<span>{t('Settings.EInvoice.Table.AdditionalData.title')}</span>}>
                            <div className="info__row">
                                <label className="info__row-name">
                                    {t('Settings.EInvoice.Table.defaultClassificationCode')}
                                    <OfferionTooltip
                                        placement="right"
                                        titleKey={'Settings.EInvoice.Table.defaultClassificationCode'}
                                        bodyKey={'Settings.EInvoice.Table.DefaultClassificationCode.infoText'}
                                    />
                                </label>
                                <div className="info__row-content info__row-content--with-button">
                                    <ClassificationCodeInput
                                        name="defaultClassificationCode"
                                        placeholder={t('Settings.EInvoice.Table.DefaultClassificationCodePlaceholder')}
                                    />
                                </div>
                                <OfferionErrorMessage name="defaultClassificationCode" />
                            </div>
                            <div className="info__row">
                                <label className="info__row-name" htmlFor="">
                                    {t('Settings.EInvoice.Table.AdditionalIBAN')}
                                    <OfferionTooltip
                                        iconStyle={{ marginBottom: '7px' }}
                                        titleKey="Settings.EInvoice.Table.AdditionalIBAN.infoTitle"
                                        bodyKey="Settings.EInvoice.Table.AdditionalIBAN.infoText"
                                    />
                                </label>
                                <div className="info__row-content info__row-content--with-button">
                                    <Field name="secondBankAccount" type="text" />
                                </div>
                                <OfferionErrorMessage name="secondBankAccount" />
                            </div>
                        </CollapsibleSection>
                    </div>
                    <div className="main__footer">
                        <div className="main__footer-buttons main__footer-buttons--mobile-only">
                            <div className="button-outer">
                                <button className="button button--white button--padded" type="submit">
                                    <span className="icon icon__save"></span>
                                    <span className="button-text">{t('Allaround.Button.save')}</span>
                                </button>
                            </div>
                            <div className="button-outer">
                                <button className="button button--white button--padded" type="button" onClick={() => navigate(-1)}>
                                    <span className="icon icon__delete"></span>
                                    <span className="button-text">{t('Allaround.Button.cancel')}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </Form>
            </Formik>
        </main>
    )
}
